/**
 * Výběr výdejního místa PPL přes web komponentu <ppl-access-point-widget>.
 *
 * Loader ani widget se nenačítají, dokud zákazník opravdu nezvolí dopravu PPL —
 * na stránkách bez téhle dopravy modul neudělá nic. Vybrané místo se ukládá do
 * sdíleného hidden inputu ve tvaru, který čte PHP (ploché klíče), a kompletní
 * odpověď widgetu se veze s sebou pod klíčem rawData pro pozdější využití.
 */

interface PplAccessPointAddress {
    street?: string;
    city?: string;
    zipCode?: string;
}

interface PplAccessPointPaymentMethods {
    cash?: boolean;
    card?: boolean;
}

interface PplAccessPoint {
    code?: string;
    name?: string;
    address?: PplAccessPointAddress;
    paymentMethods?: PplAccessPointPaymentMethods;
}

interface PplWidgetElement extends HTMLElement {
    open(): void;
    close(): void;
}

const pplWidgetTag = 'ppl-access-point-widget';
const pplLoaderUrl = 'https://www.ppl.cz/accesspointwidget/loader.js';
const pplDeliveryType = 'PPL Box';

let pplWidget: PplWidgetElement | null = null;
let pplLoader: Promise<void> | null = null;
let pplWidgetReady: Promise<void> | null = null;

function getPplInput(): HTMLInputElement | null {
    return document.querySelector('[data-delivery-type="' + pplDeliveryType + '"]');
}

function getPplLocationInput(): HTMLInputElement | null {
    return document.querySelector('input[name="deliveryLocation"]');
}

function getPplApiKey(): string {
    const holder = document.querySelector('[data-ppl-widget-key]');

    return holder !== null ? (holder.getAttribute('data-ppl-widget-key') ?? '') : '';
}

function pplText(value: string | undefined): string {
    return typeof value === 'string' ? value : '';
}

/**
 * Widget vrací všechna pole jako volitelná, proto se ploché klíče skládají
 * defenzivně — PHP na nich staví adresu i validaci dobírky.
 */
function mapPplPoint(point: PplAccessPoint): object {
    const address = point.address ?? {};
    const paymentMethods = point.paymentMethods ?? {};
    const street = pplText(address.street);
    const city = pplText(address.city);

    return {
        code: pplText(point.code),
        name: pplText(point.name),
        zipCode: pplText(address.zipCode),
        street: street,
        city: city,
        // CMS čte z uloženého místa právě tenhle klíč a očekává řetězec.
        address: [street, city].filter((part: string): boolean => part !== '').join(', '),
        activeCardPayment: paymentMethods.card === true,
        activeCashPayment: paymentMethods.cash === true,
        rawData: point,
    };
}

function loadPplWidget(): Promise<void> {
    if (pplLoader === null) {
        pplLoader = new Promise<void>(function (resolve, reject): void {
            if (customElements.get(pplWidgetTag) !== undefined) {
                resolve();

                return;
            }

            const script = document.createElement('script');
            script.src = pplLoaderUrl;
            script.async = true;
            script.addEventListener('load', function (): void {
                resolve();
            });
            script.addEventListener('error', function (): void {
                reject(new Error('Nepodařilo se načíst loader widgetu PPL.'));
            });

            document.head.appendChild(script);
        }).then(function (): Promise<void> {
            return customElements.whenDefined(pplWidgetTag).then(function (): void {
                return undefined;
            });
        });
    }

    return pplLoader;
}

function createPplWidget(apiKey: string): PplWidgetElement {
    if (pplWidget === null) {
        const widget = document.createElement(pplWidgetTag) as PplWidgetElement;
        widget.setAttribute('api-key', apiKey);
        // Administrace klíče může mít nastavený inline režim, pokladna potřebuje modal.
        widget.setAttribute('config', JSON.stringify({viewMode: 'modal'}));

        // Po vložení do stránky si widget teprve tahá konfiguraci ze serveru PPL
        // a open() zavolané dřív než dorazí ready se tiše zahodí. Chybu bereme
        // taky jako konec čekání, jinak by první otevření viselo navždy.
        pplWidgetReady = new Promise<void>(function (resolve): void {
            widget.addEventListener('ppl-accesspointwidget-ready', function (): void {
                resolve();
            });
            widget.addEventListener('ppl-accesspointwidget-error', function (): void {
                resolve();
            });
        });

        // Listenery musí být navěšené dřív, než se element dostane do DOM.
        widget.addEventListener('ppl-accesspointwidget-select', processPplResponse as EventListener);
        widget.addEventListener('ppl-accesspointwidget-error', logPplError as EventListener);

        document.body.appendChild(widget);
        pplWidget = widget;
    }

    return pplWidget;
}

function openPplWidget(): void {
    const apiKey = getPplApiKey();

    if (apiKey === '') {
        console.error('Chybí API klíč widgetu PPL (data-ppl-widget-key).');

        return;
    }

    loadPplWidget().then(function (): Promise<void> {
        createPplWidget(apiKey);

        return pplWidgetReady ?? Promise.resolve();
    }).then(function (): void {
        // Než se widget načetl, mohl zákazník přepnout na jinou dopravu.
        if (isPplDeliverySelected()) {
            pplWidget?.open();
        }
    }).catch(function (error: Error): void {
        // Bez zahození promise by se po výpadku sítě už nikdy nezkusilo znovu.
        pplLoader = null;
        console.error(error.message);
    });
}

function isPplDeliverySelected(): boolean {
    const pplInput = getPplInput();

    return pplInput !== null && pplInput.checked;
}

function openPplWidgetIfNeeded(event: Event): void {
    const target = event.target as HTMLInputElement;
    const isDeliveryMethodCheckbox = target.type === 'checkbox' && target.name === 'deliveryMethod';

    if (isDeliveryMethodCheckbox) {
        if (target.dataset.deliveryType === pplDeliveryType && target.checked) {
            openPplWidget();
        } else {
            clearPplLocation();
        }
    }
}

function clearPplLocation(): void {
    const locationInput = getPplLocationInput();

    if (locationInput !== null) {
        locationInput.value = '';
    }

    setPplName('');
}

function processPplResponse(event: CustomEvent<PplAccessPoint | null>): void {
    const point = event.detail ?? {};
    const locationInput = getPplLocationInput();

    if (locationInput !== null) {
        locationInput.value = JSON.stringify({pplPoint: mapPplPoint(point)});
    }

    setPplName(pplText(point.name));
    pplWidget?.close();
}

function logPplError(event: CustomEvent<{code?: string; message?: string} | null>): void {
    console.error('Chyba widgetu PPL:', event.detail?.code, event.detail?.message);
}

function setPplName(name: string): void {
    const pplInput = getPplInput();
    const form = pplInput !== null ? pplInput.form : null;

    if (pplInput === null || form === null) {
        return;
    }

    const nameHolder = form.querySelector('[data-delivery-location="' + pplInput.value + '"]');

    if (nameHolder !== null) {
        nameHolder.textContent = name;
    }
}

function initializePpl(): void {
    const pplAvailable = getPplInput();

    if (pplAvailable !== null) {
        pplAvailable.form?.addEventListener('change', openPplWidgetIfNeeded);
    }
}

document.addEventListener('DOMContentLoaded', function (): void {
    initializePpl();
});
